// src/contexts/RecorderContext.js
import React, { createContext, useContext, useState, useEffect, useRef, useCallback } from 'react';
import { toast } from 'react-toastify';
import { AudioRecorderService } from '../services/audio/AudioRecorderService';

const RecorderContext = createContext(null);

export const useRecorder = () => {
  const context = useContext(RecorderContext);
  if (!context) {
    throw new Error('useRecorder must be used within a RecorderProvider');
  }
  return context;
};

// eslint-disable-next-line max-lines-per-function
export const RecorderProvider = ({ children }) => {
  // Recording state
  const [isRecording, setIsRecording] = useState(false);
  const [isPaused, setIsPaused] = useState(false);
  const [duration, setDuration] = useState('0:00');
  const [durationMs, setDurationMs] = useState(0);
  const [volumeLevel, setVolumeLevel] = useState(0);
  const [estimatedSize, setEstimatedSize] = useState('0 KB');

  // Post-recording state
  const [audioBlob, setAudioBlob] = useState(null);
  const [audioUrl, setAudioUrl] = useState(null);
  const [showMetadataModal, setShowMetadataModal] = useState(false);

  // Recording context
  const [recordingEntityId, setRecordingEntityId] = useState(null);
  const [recordingEntityName, setRecordingEntityName] = useState(null);

  // Ready state (pre-recording, showing controls but not yet recording)
  const [isReady, setIsReady] = useState(false);

  // Expanded state for floating recorder
  const [isExpanded, setIsExpanded] = useState(false);

  // Refs
  const recorderRef = useRef(null);
  const durationIntervalRef = useRef(null);

  // Max duration: 2 hours
  const MAX_DURATION = 7200000;

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (recorderRef.current) {
        recorderRef.current.destroy();
      }
      if (durationIntervalRef.current) {
        clearInterval(durationIntervalRef.current);
      }
    };
  }, []);

  // Navigation guard - warn user about unsaved recording
  useEffect(() => {
    const handleBeforeUnload = (e) => {
      if (isRecording || showMetadataModal) {
        e.preventDefault();
        e.returnValue = 'You have an unsaved recording. Are you sure you want to leave?';
        return e.returnValue;
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [isRecording, showMetadataModal]);

  // Start recording
  const startRecording = useCallback(async (entityId, entityName) => {
    if (!entityId) {
      toast.error('Please select an entity first');
      return false;
    }

    // Store entity context
    setRecordingEntityId(entityId);
    setRecordingEntityName(entityName);

    // Initialize recorder if needed
    if (!recorderRef.current) {
      recorderRef.current = new AudioRecorderService();
    }

    const success = await recorderRef.current.start({
      onStart: () => {
        setIsRecording(true);
        setIsPaused(false);
        setIsReady(false);
        setIsExpanded(false);

        // Start duration timer
        durationIntervalRef.current = setInterval(() => {
          const formattedDuration = recorderRef.current.getFormattedDuration();
          const currentDurationMs = recorderRef.current.getDuration();
          setDuration(formattedDuration);
          setDurationMs(currentDurationMs);

          // Estimate size (Opus at ~3KB/sec)
          const estimatedBytes = (currentDurationMs / 1000) * 3 * 1024;
          if (estimatedBytes < 1024 * 1024) {
            setEstimatedSize(`${(estimatedBytes / 1024).toFixed(0)} KB`);
          } else {
            setEstimatedSize(`${(estimatedBytes / 1024 / 1024).toFixed(1)} MB`);
          }

          // Check max duration
          if (currentDurationMs >= MAX_DURATION) {
            stopRecording();
            toast.warning('Maximum recording duration reached (2 hours)');
          }
        }, 100);
      },
      onStop: async (data) => {
        setIsRecording(false);
        setIsPaused(false);

        // Clear duration timer
        if (durationIntervalRef.current) {
          clearInterval(durationIntervalRef.current);
          durationIntervalRef.current = null;
        }

        // Store audio data
        setAudioBlob(data.blob);
        setAudioUrl(data.url);

        // Show metadata modal
        setShowMetadataModal(true);
      },
      onPause: () => {
        setIsPaused(true);
      },
      onResume: () => {
        setIsPaused(false);
      },
      onError: (error) => {
        setIsRecording(false);
        toast.error(`Recording error: ${error}`);
      },
      onVisualizationData: (data) => {
        setVolumeLevel(data.volume);
      }
    });

    if (!success) {
      toast.error('Failed to start recording. Please check microphone permissions.');
    }

    return success;
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Pause recording
  const pauseRecording = useCallback(() => {
    if (recorderRef.current?.pause()) {
      setIsPaused(true);
      return true;
    }
    return false;
  }, []);

  // Resume recording
  const resumeRecording = useCallback(() => {
    if (recorderRef.current?.resume()) {
      setIsPaused(false);
      return true;
    }
    return false;
  }, []);

  // Stop recording
  const stopRecording = useCallback(() => {
    if (recorderRef.current) {
      recorderRef.current.stop();
      return true;
    }
    return false;
  }, []);

  // Cancel recording
  const cancelRecording = useCallback(() => {
    if (isRecording && recorderRef.current) {
      recorderRef.current.stop();
    }

    // Reset all state
    setIsRecording(false);
    setIsPaused(false);
    setIsReady(false);
    setAudioBlob(null);
    setAudioUrl(null);
    setDuration('0:00');
    setDurationMs(0);
    setEstimatedSize('0 KB');
    setVolumeLevel(0);
    setShowMetadataModal(false);
    setIsExpanded(false);

    // Clear timer
    if (durationIntervalRef.current) {
      clearInterval(durationIntervalRef.current);
      durationIntervalRef.current = null;
    }
  }, [isRecording]);

  // Discard recording (after stopping)
  const discardRecording = useCallback(() => {
    setAudioBlob(null);
    setAudioUrl(null);
    setDuration('0:00');
    setDurationMs(0);
    setEstimatedSize('0 KB');
    setShowMetadataModal(false);
    setRecordingEntityId(null);
    setRecordingEntityName(null);
  }, []);

  // Called after successful save
  const onSaveComplete = useCallback(() => {
    setAudioBlob(null);
    setAudioUrl(null);
    setDuration('0:00');
    setDurationMs(0);
    setEstimatedSize('0 KB');
    setShowMetadataModal(false);
    setRecordingEntityId(null);
    setRecordingEntityName(null);
  }, []);

  // Show recorder in ready state (pre-recording)
  const showRecorder = useCallback((entityId, entityName) => {
    if (!entityId) {
      toast.error('Please select an entity first');
      return;
    }
    setRecordingEntityId(entityId);
    setRecordingEntityName(entityName);
    setIsReady(true);
    setIsExpanded(true);
  }, []);

  // Dismiss ready state
  const dismissRecorder = useCallback(() => {
    setIsReady(false);
    setIsExpanded(false);
    setRecordingEntityId(null);
    setRecordingEntityName(null);
  }, []);

  // Toggle expanded state
  const toggleExpanded = useCallback(() => {
    setIsExpanded(prev => !prev);
  }, []);

  // Get recorder service for compression (used by metadata modal)
  const getRecorderService = useCallback(() => {
    return recorderRef.current;
  }, []);

  const value = {
    // Recording state
    isRecording,
    isPaused,
    duration,
    durationMs,
    volumeLevel,
    estimatedSize,

    // Post-recording state
    audioBlob,
    audioUrl,
    showMetadataModal,
    setShowMetadataModal,

    // Recording context
    recordingEntityId,
    recordingEntityName,

    // UI state
    isReady,
    isExpanded,
    toggleExpanded,

    // Actions
    showRecorder,
    dismissRecorder,
    startRecording,
    pauseRecording,
    resumeRecording,
    stopRecording,
    cancelRecording,
    discardRecording,
    onSaveComplete,
    getRecorderService,
  };

  return (
    <RecorderContext.Provider value={value}>
      {children}
    </RecorderContext.Provider>
  );
};

export default RecorderContext;
